import React, { useRef } from 'react';
import { Clock, User, Bot, AlertCircle, RefreshCw } from 'lucide-react';
import { ConversationMessage } from '../../../types/recruiter';

interface ConversationLogProps {
  conversationLog: ConversationMessage[];
  loading: boolean;
  error: string | null;
  onRefetch: () => void;
}

export const ConversationLog: React.FC<ConversationLogProps> = ({
  conversationLog,
  loading,
  error,
  onRefetch,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('ja-JP', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return timestamp;
    }
  };

  const getSpeakerIcon = (speaker: string) => {
    switch (speaker) {
      case 'interviewer':
        return <Bot className="w-5 h-5 text-blue-600" />;
      case 'candidate':
        return <User className="w-5 h-5 text-green-600" />;
      case 'system':
        return <AlertCircle className="w-5 h-5 text-orange-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getSpeakerName = (speaker: string) => {
    switch (speaker) {
      case 'interviewer':
        return 'AI面接官';
      case 'candidate':
        return '候補者';
      case 'system':
        return 'システム';
      default:
        return speaker;
    }
  };

  const getSpeakerBubbleClass = (speaker: string) => {
    switch (speaker) {
      case 'interviewer':
        return 'bg-blue-50 border-blue-200 text-blue-900';
      case 'candidate':
        return 'bg-green-50 border-green-200 text-green-900';
      case 'system':
        return 'bg-orange-50 border-orange-200 text-orange-900';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-900';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-3">
          <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />
          <span className="text-gray-600">会話ログを読み込み中...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">エラーが発生しました</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={onRefetch}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          再読み込み
        </button>
      </div>
    );
  }

  if (!conversationLog || conversationLog.length === 0) {
    return (
      <div className="text-center py-12">
        <Bot className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">会話ログがありません</h3>
        <p className="text-gray-600">この面接の会話ログはまだ生成されていません。</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5 text-blue-600" />
          <h4 className="text-lg font-semibold text-gray-900">会話ログ</h4>
          <span className="text-sm text-gray-500">({conversationLog.length}件)</span>
        </div>
        <button
          onClick={onRefetch}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          title="再読み込み"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto space-y-4 pr-2"
        style={{ maxHeight: 'calc(100vh - 400px)' }}
      >
        {conversationLog.map((message, index) => (
          <div key={index} className="flex gap-3">
            <div className="flex-shrink-0 mt-1">{getSpeakerIcon(message.speaker)}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium text-gray-700">
                  {getSpeakerName(message.speaker)}
                </span>
                {message.timestamp && (
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatTimestamp(message.timestamp)}
                  </span>
                )}
              </div>
              <div className={`rounded-lg p-3 border ${getSpeakerBubbleClass(message.speaker)}`}>
                <div className="text-sm leading-relaxed whitespace-pre-wrap">{message.message}</div>
                {message.type === 'system_event' && message.event && (
                  <div className="mt-2 pt-2 border-t border-orange-200">
                    <span className="text-xs font-medium text-orange-700">
                      イベント: {message.event}
                    </span>
                    {message.reason && (
                      <div className="text-xs text-orange-600 mt-1">理由: {message.reason}</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
